<template>
  <dara-article-layout v-if="article">
    <template #center>
      <dara-editor
        ref="editor"
        mode="edit"
        :article="article"
        :change="onChange" />
    </template>
    <template #right>
      <div class="dara-article-sidebar">
        <dara-form v-slot="{ validate }" class="d-block ps-3" :submit="prepareArticle">
          <div v-if="(isAnswer || question_id) && department" class="mb-2">
            {{ $t('department') }}: "<strong>{{ department.name }}</strong>"
          </div>

          <dara-department-chooser
            v-else
            ref="departmentChooser"
            v-model="article.department_id"
            @on-change="onDepartmentChange"
            @onNewDepartment="onNewDepartment()" />

          <div v-if="isAnswer || question" class="mb-3">
            <dara-p v-if="question_id" weight="strong">
              {{ question.access === 0 ? $t('this-will-be-a-public-article') : $t('this-will-be-a-department-only-article') }}
            </dara-p>
            <dara-p v-else weight="strong">
              {{ article.access === 0 ? $t('this-will-be-a-public-article') : $t('this-will-be-a-department-only-article') }}
            </dara-p>
          </div>

          <div v-else class="mb-4 mt-4">
            {{ $t('access') }}*
            <dara-select
              v-bind="$attrs"
              :name="$t('access-field')"
              rules="required"
              :value="article.access"
              class="mt-1 mb-0"
              @on-change="onAccessChange">
              <option :value="null" disabled>{{ $t('select-access') }}</option>
              <option
                v-for="option in all_access"
                :key="option.name"
                :value="option.value">
                {{ option.name }}
              </option>
            </dara-select>
            <small class="text-muted dara-u-text--small d-block mt-1 mb-3">
              {{ $t(accessesInfo[article.access]) }}
            </small>
          </div>

          <div class="mb-4 mt-4">
            <dara-p class="cursor--pointer" color="base">
              <div @click="showTips()">
                <strong>{{ $t('how-to-write-good-article') }}</strong>
                <dara-icon class="ms-3" size="sm" :icon="tipsIcon" />
              </div>
            </dara-p>
            <ul v-if="shownTips" class="text-muted tips">
              <li>{{ $t('first-tip') }}</li>
              <li>{{ $t('second-tip') }}</li>
              <li>{{ $t('third-tip') }}</li>
              <li>{{ $t('fourth-tip') }}</li>
              <li>{{ $t('fifth-tip') }}</li>
              <li>{{ $t('sixth-tip') }}</li>
              <li>{{ $t('article-tags-info1') }} {{ $t('article-tags-info2') }}</li>
            </ul>
          </div>

          <div class="row">
            <div class="col text-end">
              <dara-button
                native-type="submit"
                class="btn btn-outline-success"
                :disabled="!hasChanges || isPublishing"
                @click="validate">
                {{ $t('save-and-publish') }}
              </dara-button>
            </div>
          </div>
        </dara-form>
      </div>
      <dara-modal-new-department
        v-if="showModal()"
        ref="departmentModal"
        @close="closeModal()">
      </dara-modal-new-department>
    </template>
  </dara-article-layout>
</template>

<script>
import { goToArticleNotFound, goToForbiddenArticle, goToOpenArticle, goToDeniedArticle } from '@/modules/article/article.routes';
import { saveArticle, getArticleById, replaceHrefContentArticle, isAnswer, belongsToSomeTrack } from '@/modules/article/article.service';
import { getQuestion } from '@/modules/question/question.service';
import { getDepartmentById } from '@/modules/department/department.service';
import { translate } from '@/services/translate.service';
import { toastError, toastWarning } from '@/services/toastService';
import { getters } from '@/modules/user/user.store';
import { saveTagsFromContent } from '@/modules/tag/tag.service';
import { ARTICLE_ACCESS_INFO, ARTICLE_ERRORS } from '@/modules/article/article.constants';
import { USERS_ROLES } from '@/modules/user/user.constants';
import { removeHtmlTags } from '@/helpers/text';

import DaraEditor from '@/components/editor/Editor.vue';
import DaraArticleLayout from '@/modules/article/components/ArticleLayout.vue';
import DaraDepartmentChooser from '@/modules/department/components/DepartmentChooser.vue';

export default {
  name: 'DaraArticleEditPage',
  components: {
    DaraEditor,
    DaraArticleLayout,
    DaraDepartmentChooser,
  },
  data() {
    return {
      user: getters.getUser(),
      department: null,
      question: null,
      error: null,
      article: null,
      contentWithTags: '',
      changed: false,
      isPublishing: false,
      isAnswer: false,
      hasTrack: false,
      all_access: [
        { name: translate('public'), value: 0 },
        { name: translate('department-only'), value: 1 },
        { name: translate('private'), value: 2 },
      ],
      accessesInfo: ARTICLE_ACCESS_INFO,
      newDepartment: false,
      shownTips: 1,
    };
  },
  computed: {
    id() {
      return this.$route.params?.id;
    },
    question_id() {
      return this.$route.params?.question_id;
    },
    hasChanges() {
      return this.changed;
    },
    tipsIcon() {
      return this.shownTips ? 'chevron-down' : 'chevron-right';
    },
  },
  mounted() {
    if (this.id) {
      if (this.user.permission < USERS_ROLES.WRITER) {
        goToForbiddenArticle(this.$router, false);
      } else {
        this.fetchArticle();
      }
    } else {
      if (this.user.permission < USERS_ROLES.WRITER) goToForbiddenArticle(this.$router, true);
      if (this.question_id) this.fetchQuestion();
      this.setNewArticle();
    }
  },
  methods: {
    fetchArticle() {
      if (this.article) return;
      getArticleById(this.id)
        .then(data => {
          this.article = data.data;
          if (!(this.user.permission > USERS_ROLES.WRITER || (this.user.permission === USERS_ROLES.WRITER && this.article.user_id === this.user.id))) {
            goToForbiddenArticle(this.$router, false);
          } else {
            this.fetchDepartment(this.article.department_id);
            this.checkIfIsAnswer();
            this.checkIfBelongsToSomeTrack();
          }
        })
        .catch(err => {
          if (err.response.data.code === '22P02' || (err.response.data.errors === ARTICLE_ERRORS[err.response.status] && err.response.status === 404)) {
            goToArticleNotFound(this.$router);
          } else if (err.response.data.errors === ARTICLE_ERRORS[err.response.status] && err.response.status === 403) {
            goToDeniedArticle(this.$router);
          }
          this.article = null;
        });
    },
    fetchQuestion() {
      getQuestion(this.question_id)
        .then(({ data }) => {
          this.question = data;
          this.onAccessChange(data.access);
          this.fetchDepartment(this.question.department_id);
        })
        .catch();
    },
    fetchDepartment(departmentId) {
      getDepartmentById(departmentId)
        .then(data => {
          this.department = data;
        })
        .catch();
    },
    setNewArticle() {
      if (this.id) return;
      this.article = {};
    },
    prepareArticle() {
      this.isPublishing = true;
      if (this.hasChanges) {
        if (!this.article.content) {
          this.isPublishing = false;
          toastError(translate('please-write-a-title-for-your-article'));
          return;
        }
        if (!this.department) {
          this.isPublishing = false;
          toastError(translate('please-choose-a-department-for-your-article'));
          return;
        }

        this.article.tags = [];
        this.article.content.forEach(block => {
          if (block.type === 'list') {
            block.data.items.forEach(item => {
              const containTags = item.match(/#[^#\s]+/gi);
              if (containTags) {
                this.contentWithTags = this.contentWithTags.concat(item, ' ');
              }
            });
          } else if (block.type === 'header' || block.type === 'paragraph') {
            const containTags = block.data.text.match(/#[^#\s]+/gi);
            if (containTags) {
              this.contentWithTags = this.contentWithTags.concat(block.data.text, ' ');
            }
          }
        });

        this.article.department_id = this.department.id;
        this.article.question_id = this.question_id;
        this.article.content = replaceHrefContentArticle(this.article.content);

        if (this.contentWithTags !== '') {
          // SAVING TAGS BEFORE ARTICLES
          saveTagsFromContent(removeHtmlTags(this.contentWithTags))
            .then(tags => {
              this.article.tags = [];
              tags.forEach(tag => {
                if (tag.id) { this.article.tags.push(tag); }
              });
              this.saveArticle();
            });
        } else {
          this.saveArticle();
        }
      }
    },
    saveArticle() {
      saveArticle({ ...this.article })
        .then(article => goToOpenArticle(this.$router, article || this.article))
        .catch(err => {
          if (err.response && err.response.data.errors === ARTICLE_ERRORS[err.response.status] && err.response.status === 401) {
            this.isPublishing = false;
            toastError(translate('you-dont-have-permission-to-edit-this-article'));
          } else if (!err.response) {
            switch (err) {
            case 'no-title':
              this.isPublishing = false;
              toastError(translate('please-write-a-title-for-your-article'));
              break;
            case 'no-content':
              this.isPublishing = false;
              toastError(translate('your-article-should-have-some-content'));
              break;
            case 'no-department':
              this.isPublishing = false;
              toastError(translate('please-choose-a-department-for-your-article'));
              break;
            default:
              this.isPublishing = false;
              toastError(translate('error-on-saving-article'));
              break;
            }
          }
        });
    },
    checkIfIsAnswer() {
      isAnswer(this.id)
        .then(({ data }) => {
          this.isAnswer = data;
        });
    },
    checkIfBelongsToSomeTrack() {
      belongsToSomeTrack(this.id)
        .then(({ data }) => {
          this.hasTrack = data;
        });
    },
    onChange(content) {
      this.changed = true;
      this.article.content = content.blocks;
    },
    onDepartmentChange(department) {
      this.changed = true;
      this.department = department;
      if (this.hasTrack) {
        toastWarning(translate('the-article-belong-to-a-track-be-careful-changing-department'));
      }
    },
    onAccessChange(access) {
      this.changed = true;
      this.$set(this.article, 'access', access);
      if (this.hasTrack) {
        toastWarning(translate('the-article-belong-to-a-track-be-careful-changing-access'));
      }
    },
    onNewDepartment() {
      this.newDepartment = true;
    },
    showModal() {
      return this.newDepartment;
    },
    closeModal() {
      this.$refs.departmentChooser.onfetch();
      this.newDepartment = false;
    },
    showTips() {
      this.shownTips = !this.shownTips;
    },
  },
};
</script>

<style scoped>
  .tips {
    font-size: 14px;
    margin-left: -15px;
  }

  .dara-article-sidebar {
    position: fixed;
    width: 30%;
  }
  @media only screen and (max-width: 768px) {
    .dara-article-sidebar {
      position: inherit;
    }
  }
</style>
